/**
 * reportSelectors
 *
 */
import _ from 'underscore';
import moment from 'moment';
import { createSelector } from 'reselect';
import { hasReportTypePermissions } from '../common/classes/permissions';
import store from '../../reducers/storeRegistration';
import { checkPermission } from '../../../behaviors/src/lib/permissions';

/* @method - _getNode - top down approach of starting with topmost parent
 *   and if nodeList is passed in, it gets added to that list.
 */
const getNode = (object, nodeList, entity, idField = 'id', nameField = 'name') => {
  let entityName = entity;
  if (_.isNull(entityName)) {
    entityName = entity;
  }
  // create object node if we don't already have one.
  if (_.findIndex(nodeList, item => object[idField] === item.id) === -1) {
    if (object[idField] && _.isString(object[nameField])) {
      // keep original object data.  That gives us the ability to select
      // other attributes later on if we want.
      const newnode = {
        id: object[idField],
        name: object[nameField],
        entity: entityName,
        nodes: []
      };

      // we did not find a job for this datum,
      // need to add it to the nodeLists.
      nodeList.push(newnode);
    }
  }
  const index = _.findIndex(nodeList, node => object[idField] === node.id);
  return nodeList[index];
};

/* functions used for job piece filter input.
 *
 */
const makePieceTree = data => {
  const result = [];
  data.forEach(elem => {
    const section = getNode(elem, result, 'JobPieceSection', 'sectionName', 'sectionName', true);
    if (section) {
      getNode(elem, section.nodes, 'JobPiece', 'id', 'name');
    }
  });
  return result;
};
const makePieceOtherTree = data => {
  const result = [];
  data.forEach(elem => {
    const otherParent = {
      name: 'Other',
      id: 'OtherParent',
      value: 'OtherParent',
      entity: 'JobPiecesOtherParent'
    };
    const parent = getNode(otherParent, result, 'JobPiecesOtherParent', 'id', 'name', true);
    if (parent) {
      getNode(elem, parent.nodes, 'JobPieceOther', 'id', 'name');
    }
  });
  return result;
};

/* @method - _makeTree.  data is coming back often in flat rows that we need
 * to build special hiearchical representations from.
 */
const makeTree = (pieceList, pieceOtherList) => {
  const structuredPiece = makePieceTree(pieceList);
  const structuredPieceOther = makePieceOtherTree(pieceOtherList);
  const result = _.map(structuredPiece, option => option);
  if (structuredPieceOther.length > 0) {
    result.push(structuredPieceOther[0]);
  }
  return result;
};

/* eslint-disable arrow-body-style */
const pieces = state => {
  return state.jobPieceFilterReport && state.jobPieceFilterReport.list ? state.jobPieceFilterReport.list : [];
};
/* eslint-enable arrow-body-style */

/* eslint-disable arrow-body-style */
const pieceOthers = state => {
  return state.pieceOtherFilterReport && state.pieceOtherFilterReport.list ? state.pieceOtherFilterReport.list : [];
};
/* eslint-enable arrow-body-style */

export const getStructuredPieces = createSelector([pieces, pieceOthers], (pieceList, pieceOtherList) => makeTree(pieceList, pieceOtherList));

/**
 * @method getReportNumber
 * create "reportNumber" value from the report's appCreated, jobNumber, and reportNumber columns
 */
export const getReportNumber = data => {
  const {
    appCreated,
    created,
    jobNumber,
    reportNumber
  } = data;
  let job = jobNumber;
  if (!job && data.job && data.job.jobNumber) {
    job = data.job.jobNumber;
  }
  return `${job}-${moment(created || appCreated).format('YYYYMMDD')}-${reportNumber}`;
};

/**
 * @method getReportNumberFromIssue
 * create "reportNumber" from an Issue
 */
export const getReportNumberFromIssue = issue => getReportNumber({
  appCreated: issue.reportCreatedDate,
  jobNumber: issue.jobNumber,
  reportNumber: issue.reportNumber
});

/* eslint-disable  no-underscore-dangle */
const activeSelector = state => state.report.active;
const statusSelector = state => {
  let result = null;
  const currentActive = state.report.active;
  if (currentActive && currentActive.content && currentActive.content.statusHistory) {
    const sh = currentActive.content.statusHistory;
    const ids = Object.keys(sh);
    if (ids.length > 0) {
      result = sh[ids[0]];
      ids.forEach(id => {
        const status = sh[id];
        if (status._created > result._created) {
          result = status;
        }
      });
    }
  }
  return result;
};
export const checkCanEditPermissions = (user, reportType, status) => {
  let result;
  const {
    permissions = {}
  } = user;
  const {
    identifier: reportTypeIdentifier
  } = reportType;
  const {
    identifier: statusIdentifier
  } = status;
  if (statusIdentifier === 'draft' || statusIdentifier === 'rejected') {
    // not submitted yet, so we are good to edit.
    result = true;
  } else if (statusIdentifier === 'submitted') {
    result = hasReportTypePermissions(reportTypeIdentifier, 'report_edit_submitted');
  }
  if (permissions.userType === 'asset_owner') {
    result = false;
  }
  // if one of the criteria above failed, we do not have permission.
  // if undefined, we keep going.
  if (result === false) {
    return false;
  }

  // fabricate content for permission call - could be a report row that doesn have it yet.
  //
  const entity = {
    reportType,
    status
  };

  // check // deny_report_edit or allow_report_edit
  // construct entity with reportType and
  // current status.
  const editPerm = checkPermission(entity, 'edit', user.permissions, user);
  // if editPerm is definitive, it will override previous
  if (editPerm !== undefined) {
    return editPerm;
  }

  // this will return only true or false - not undefined.
  return result === true;
};
export const canEditSelector = createSelector([activeSelector, statusSelector], (reportContent, status) => {
  if (reportContent.content === undefined || reportContent.content === null || status === undefined) {
    return false;
  }
  const {
    login
  } = store.getState();
  const user = login.user || {};
  const {
    content: {
      _readonly: {
        reportType
      } = {}
    } = {}
  } = reportContent;
  return checkCanEditPermissions(user, reportType, status);
});

/* eslint-enable  no-underscore-dangle */